import { Component, Vue, VModel, Prop } from 'vue-property-decorator';
import { groupBy } from 'lodash';
import { ASSESSMENT_PERIOD_STATUSES, NOTIFICATION, PROJECT_TYPES } from '@/constants';
import { convertNumberToFourDigitsString, extractQuarterFromName, toTreeOptionsByQuarters } from '@/helpers';
import {
  AssessmentPeriodDTO,
  ProjectDTO,
  AssessmentPeriodsExportData,
  TreeOption,
  AssessmentPeriodsExportSummary
} from '@/models';
import { getActualEffort, getUserDisplayInfo } from '@/services';
import { NA } from '@/services/project-exporting-config';
import AssessmentPeriodsExportService from '@/services/assessment-periods-export';
import RewardPeriodsExport from '@/services/reward-periods-export';
import TreeFilterDialog from '@/components/common/tree-filter-dialog/TreeFilterDialog.vue';

@Component({
  components: {
    TreeFilterDialog,
    RewardPeriodsExport
  }
})
export default class AssessmentPeriodsExport extends Vue {
  @VModel() modalShown!: boolean;
  @Prop({ default: () => [] }) assessmentPeriods: AssessmentPeriodDTO[];
  @Prop({ default: () => [] }) projects: ProjectDTO[];

  private isExporting = false;

  getProjectDrop(assessmentPeriod: AssessmentPeriodDTO, project: ProjectDTO): number | null {
    const assessmentPeriodId = assessmentPeriod.id;
    const currentProjectAssessment = project.projectAssessments.find(
      projectAssessment => projectAssessment.assessmentPeriodId === assessmentPeriodId
    );
    if (currentProjectAssessment) {
      if (project.projectType === PROJECT_TYPES.INDIVIDUAL_TRACKING) {
        return (
          currentProjectAssessment.userEngagements.reduce((prev, userEngagement) => {
            return userEngagement.individualDrops ? userEngagement.individualDrops + prev : prev;
          }, 0) || null
        );
      } else {
        return currentProjectAssessment.finalDrops;
      }
    } else {
      return null;
    }
  }

  buildExportData(assessmentPeriod: AssessmentPeriodDTO, project: ProjectDTO): AssessmentPeriodsExportData {
    const { DONE } = ASSESSMENT_PERIOD_STATUSES;
    const { projectType, projectStatus, id, name, sponsor, manager } = project;
    const status = assessmentPeriod.status === DONE.label ? DONE.label : projectStatus;
    const {
      key = NA,
      plannedEffort = 0,
      userEngagements = [],
      createdAt = ''
    } = project.projectAssessments.find(pa => pa.assessmentPeriodId === assessmentPeriod.id) || {};
    const hours = getActualEffort(projectType, plannedEffort, userEngagements) || NA;
    return {
      projectId: convertNumberToFourDigitsString(id),
      projectName: name,
      projectType,
      sponsor: getUserDisplayInfo(sponsor),
      manager: getUserDisplayInfo(manager),
      hours,
      key,
      projectDrops: this.getProjectDrop(assessmentPeriod, project) || NA,
      evaluationStatus: status,
      quarter: extractQuarterFromName(assessmentPeriod.name),
      createdAt,
      numOfContributors: userEngagements.length || 0,
      contributorsIds: userEngagements.map(ue => ue.user.id)
    };
  }

  toAssessmentPeriodsExportData(
    assessmentPeriods: AssessmentPeriodDTO[],
    projects: ProjectDTO[]
  ): AssessmentPeriodsExportData[] {
    return [...assessmentPeriods]
      .sort((a, b) => {
        const firstDate = new Date(a.startDate || '');
        const secondDate = new Date(b.startDate || '');
        return firstDate.getTime() - secondDate.getTime();
      })
      .flatMap(ap => {
        const projectsInAssessmentPeriod = projects.filter(
          p => p.projectAssessments.filter(pa => pa.assessmentPeriodId === ap.id)?.length
        );
        return projectsInAssessmentPeriod.map(p => this.buildExportData(ap, p));
      });
  }

  get exportData(): AssessmentPeriodsExportData[] {
    return this.toAssessmentPeriodsExportData(this.assessmentPeriods, this.projects);
  }

  get periodOptions(): TreeOption[] {
    const quarters = [...new Set(this.exportData.map(d => d.quarter))];
    return toTreeOptionsByQuarters(quarters);
  }

  get isDisabled(): boolean {
    return this.isExporting || !this.assessmentPeriods?.length;
  }

  getTotalContributors(data: AssessmentPeriodsExportData[]): number {
    const listOfContributorsIds = new Set([...data.flatMap(d => d.contributorsIds)]);
    return listOfContributorsIds.size;
  }

  getTotalNumberDataByKey(data: AssessmentPeriodsExportData[], key: keyof AssessmentPeriodsExportData): number {
    return data.reduce((acc, cur) => acc + Number(cur[key]), 0);
  }

  toAssessmentPeriodsExportSummary(data: AssessmentPeriodsExportData[]): AssessmentPeriodsExportSummary[] {
    const groupByQuarter = groupBy(data, 'quarter');
    const periodsData = Object.entries(groupByQuarter).map(([quarter, v]) => {
      const numOfContributors = this.getTotalContributors(v);
      const totalDrops = this.getTotalNumberDataByKey(v, 'projectDrops');
      const totalHours = this.getTotalNumberDataByKey(v, 'hours');
      return {
        quarter,
        numOfContributors,
        totalHours,
        totalDrops
      };
    });
    const totalContibutors = this.getTotalContributors(data);
    const totalDrops = this.getTotalNumberDataByKey(data, 'projectDrops');
    const totalHours = this.getTotalNumberDataByKey(data, 'hours');
    const totalData = {
      quarter: 'Total',
      numOfContributors: totalContibutors,
      totalHours,
      totalDrops
    };
    return periodsData.concat(totalData);
  }

  async onModalConfirm(selectedPeriods: string[]): Promise<void> {
    this.isExporting = true;

    const finalData = selectedPeriods.length
      ? this.exportData.filter(ed => selectedPeriods.includes(ed.quarter))
      : this.exportData;

    const summaryData = this.toAssessmentPeriodsExportSummary(finalData);

    const ape = new AssessmentPeriodsExportService(finalData, summaryData);
    ape.generateXLSXFile().finally(() => {
      this.isExporting = false;
      const notificationInfo = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.exportDataSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', notificationInfo);
    });
  }

  onModalCancel(): void {
    this.modalShown = false;
  }
}
